import { useCallback, useEffect, useState } from 'react';
import { getJson } from '../lib/api.ts';

export interface ApiState<T> {
  /** Dernière réponse reçue (conservée en cas d'erreur ultérieure). */
  readonly data: T | undefined;
  readonly error: Error | null;
  readonly loading: boolean;
  readonly reload: () => void;
}

interface Settled<T> {
  readonly key: string;
  readonly path: string;
  readonly data: T | undefined;
  readonly error: Error | null;
}

/**
 * Récupère une ressource de l'API et la rafraîchit :
 * - toutes les `refreshMs` tant que la page est visible ;
 * - au retour sur l'onglet et au retour du réseau.
 * `path === null` désactive la requête.
 */
export function useApi<T>(path: string | null, refreshMs?: number): ApiState<T> {
  const [tick, setTick] = useState(0);
  const [settled, setSettled] = useState<Settled<T> | null>(null);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  const key = `${path ?? ''}#${tick}`;

  useEffect(() => {
    if (path === null) return;
    const controller = new AbortController();
    getJson<T>(path, controller.signal)
      .then((body) => setSettled({ key, path, data: body, error: null }))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const error = err instanceof Error ? err : new Error(String(err));
        // On conserve la dernière donnée reçue pour cette ressource.
        setSettled((prev) => ({ key, path, data: prev?.path === path ? prev.data : undefined, error }));
      });
    return () => controller.abort();
  }, [path, key]);

  useEffect(() => {
    if (path === null) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', reload);
    const id =
      refreshMs === undefined
        ? undefined
        : window.setInterval(() => {
            if (document.visibilityState === 'visible') reload();
          }, refreshMs);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', reload);
      if (id !== undefined) window.clearInterval(id);
    };
  }, [path, refreshMs, reload]);

  // Les données d'une autre ressource ne sont jamais affichées.
  const current = settled !== null && settled.path === path ? settled : null;
  return {
    data: current?.data,
    error: current?.error ?? null,
    loading: path !== null && settled?.key !== key,
    reload,
  };
}
